import React, { useState, useEffect } from "react";
import { Plus, Users, BookOpen, Settings } from "lucide-react";
import toast from "react-hot-toast";

import {
  getClasses,
  getClassStatistics,
  deleteClass,
} from "../services/classes";
import {
  Class,
  ClassWithDetails,
  ClassFilter,
  ClassStatistics,
} from "../types/class";

// Components
import ClassesTable from "../components/tables/ClassesTable";
import ClassForm from "../components/forms/ClassForm.tsx";
import ClassManagementModal from "../components/modals/ClassManagementModal";

const Classes: React.FC = () => {
  const [classes, setClasses] = useState<ClassWithDetails[]>([]);
  const [statistics, setStatistics] = useState<ClassStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [managingClassId, setManagingClassId] = useState<string>("");
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [filters, setFilters] = useState<ClassFilter>({
    page: 1,
    limit: 20,
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [classesResponse, statsResponse] = await Promise.all([
        getClasses(filters),
        getClassStatistics(),
      ]);

      setClasses(classesResponse.classes);
      setStatistics(statsResponse.data);
    } catch (error) {
      toast.error("Failed to load classes data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (classItem: Class) => {
    // Convert Class to the right format for editing
    setEditingClass(classItem);
    setShowForm(true);
  };

  const handleDelete = async (classId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this class? This action cannot be undone."
      )
    ) {
      try {
        await deleteClass(classId);
        toast.success("Class deleted successfully");
        loadData();
      } catch (error) {
        toast.error("Failed to delete class");
      }
    }
  };

  const handleManage = (classId: string) => {
    setManagingClassId(classId);
    setShowManagementModal(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingClass(null);
    loadData();
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingClass(null);
  };

  const getDashboardStats = () => {
    if (!statistics) return [];

    return [
      {
        title: "Total Classes",
        value: statistics.totalClasses,
        icon: <BookOpen className="w-6 h-6" />,
        color: "bg-blue-500",
      },
      {
        title: "Total Students",
        value: statistics.totalStudents,
        icon: <Users className="w-6 h-6" />,
        color: "bg-green-500",
      },
      {
        title: "Average Class Size",
        value: statistics.averageClassSize.toFixed(1),
        icon: <Users className="w-6 h-6" />,
        color: "bg-yellow-500",
      },
      {
        title: "Classes with Teachers",
        value: statistics.classesWithTeachers,
        icon: <Settings className="w-6 h-6" />,
        color: "bg-purple-500",
      },
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  // Convert API response to Class format for the table
  const convertedClasses: Class[] = classes.map((classItem: any) => {
    // Extract active students from API format
    const activeStudents = Array.isArray(classItem.students)
      ? classItem.students
          .filter((s: any) => s.status === "active")
          .map((s: any) => s.studentId || s._id || s)
      : [];

    const converted = {
      _id: classItem._id,
      name: classItem.name,
      grade: classItem.grade,
      section: classItem.section,
      academicTerm: classItem.academicTerm,
      capacity: classItem.capacity,
      subjects: classItem.subjects || [],
      students: activeStudents,
      classTeacher: classItem.classTeacher,
      schedule: classItem.schedule?.periods || [],
      // Convert backend status to isActive boolean
      isActive: classItem.status === "active",
      branch: classItem.branchId,
      createdAt: classItem.createdAt,
      updatedAt: classItem.updatedAt,
    };

    return converted;
  });

  return (
    <div className="p-6 space-y-8 min-h-screen bg-gradient-to-br from-gray-50 to-purple-50/30">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Class Management</h1>
          <p className="text-gray-600 mt-1">
            Manage classes, students, and schedules
          </p>
        </div>

        <button
          onClick={() => setShowForm(true)}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 border-0"
        >
          <Plus className="w-5 h-5" />
          <span>Add New Class</span>
        </button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {getDashboardStats().map((stat, index) => (
          <div
            key={index}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 border-0"
          >
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Grade Distribution */}
      {statistics && Object.keys(statistics.gradeDistribution).length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 border-0">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Grade Distribution</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(statistics.gradeDistribution).map(
                ([grade, count]) => (
                  <div key={grade} className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {count}
                    </div>
                    <div className="text-sm text-gray-600">Grade {grade}</div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 border-0">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Grade</span>
              </label>
              <select
                className="select select-bordered"
                value={filters.grade || ""}
                onChange={(e) =>
                  setFilters({ ...filters, grade: e.target.value || undefined })
                }
              >
                <option value="">All Grades</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((grade) => (
                  <option key={grade} value={grade.toString()}>
                    Grade {grade}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Status</span>
              </label>
              <select
                className="select select-bordered"
                value={
                  filters.isActive === undefined
                    ? ""
                    : filters.isActive.toString()
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    isActive:
                      e.target.value === ""
                        ? undefined
                        : e.target.value === "true",
                  })
                }
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Has Teacher</span>
              </label>
              <select
                className="select select-bordered"
                value={
                  filters.hasTeacher === undefined
                    ? ""
                    : filters.hasTeacher.toString()
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    hasTeacher:
                      e.target.value === ""
                        ? undefined
                        : e.target.value === "true",
                  })
                }
              >
                <option value="">All Classes</option>
                <option value="true">With Teacher</option>
                <option value="false">Without Teacher</option>
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Search</span>
              </label>
              <input
                type="text"
                className="input input-bordered"
                placeholder="Search classes..."
                value={filters.search || ""}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    search: e.target.value || undefined,
                  })
                }
              />
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => setFilters({ page: 1, limit: 20, isActive: true })}
              className="btn btn-ghost"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Classes Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border-0">
        <div className="p-1">
          <h3 className="text-lg font-semibold mb-4">Classes</h3>
          <ClassesTable
            classes={convertedClasses}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onManage={handleManage}
            onBulkDelete={async (classIds: string[]) => {
              // TODO: Implement bulk delete
            }}
          />
        </div>
      </div>

      {/* Class Form Modal */}
      {showForm && (
        <ClassForm
          isOpen={showForm}
          initialData={
            editingClass
              ? {
                  _id: editingClass._id,
                  name: editingClass.name,
                  grade: editingClass.grade,
                  section: editingClass.section,
                  academicTerm: editingClass.academicTerm,
                  capacity: editingClass.capacity,
                  subjects:
                    editingClass.subjects?.map((s) =>
                      typeof s === "string" ? s : s.name
                    ) || [],
                  students: editingClass.students,
                  teacher: editingClass.classTeacher,
                  isActive: editingClass.isActive,
                  branch: editingClass.branch,
                }
              : undefined
          }
          onClose={handleFormClose}
          onSubmit={async (classData) => {
            try {
              // TODO: Implement class creation/update
              handleFormSuccess();
            } catch (error) {
              console.error("Error saving class:", error);
            }
          }}
        />
      )}

      {/* Class Management Modal */}
      {showManagementModal && managingClassId && (
        <ClassManagementModal
          isOpen={showManagementModal}
          onClose={() => {
            setShowManagementModal(false);
            setManagingClassId("");
            loadData(); // Refresh data after management changes
          }}
          classId={managingClassId}
        />
      )}
    </div>
  );
};

export default Classes;
